import React, { CSSProperties, JSX } from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { Media } from "@domain/media/media";
import { PersonType } from "@domain/user/user";
import { fadeIn } from "@ui/styles/keyframes";
import { gap, mobile } from "@ui/styles/utils";
import { Z } from "@ui/styles/zIndex";
import { PrimaryBold, PrimaryMedium } from "@ui/components/containers/primary";
import { DeleteIcon, PlusIconRound } from "@ui/components/icons";
import { Stack } from "@ui/components/layout";
import { FileUpload } from "@ui/components/mediaEditor/fileUpload";
import { ProfileBanner } from "./profileBanner";
import { ProfilePicture } from "./profilePicture";

interface EditableProfileImageProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onError"> {
	image?: Media;
	label?: string;
	sizing?: string;
	onUpload: (media: Media, fileName: string) => void;
	onDelete: () => void;
	type: "avatar" | "banner";
	fallbackType?: PersonType;
	defaultUrl?: string;
	canBeEmpty?: boolean;
	height?: number;
	fontStyle?: CSSProperties;
	containerStyle?: CSSProperties;
	iconOpacity?: number;
	reverse?: boolean;
}

export const EditableProfileImage: React.FC<EditableProfileImageProps> = ({
	image,
	label,
	sizing,
	onUpload,
	onDelete,
	type,
	fallbackType,
	defaultUrl,
	canBeEmpty,
	height,
	fontStyle,
	containerStyle,
	iconOpacity,
	reverse,
	...htmlProps
}) => {
	const getDeleteIcon = (): JSX.Element => (
		<DeleteImageIcon onClick={onDelete} margin={type === "avatar" ? -4 : 5}>
			<DeleteIcon />
		</DeleteImageIcon>
	);

	const getAddIcon = (): JSX.Element => (
		<AddIcon iconOpacity={iconOpacity}>
			<PlusIconRound />
		</AddIcon>
	);

	return (
		<UploadContainer onUpload={onUpload} {...htmlProps}>
			{(dragging, loading) =>
				!reverse ? (
					<Row>
						<Stack align="center">
							{label && <PrimaryBold style={fontStyle ? fontStyle : undefined}>{label}</PrimaryBold>}
							{sizing && <StyledPrimaryMedium>{sizing}</StyledPrimaryMedium>}
						</Stack>
						{type === "avatar" ? (
							<StyledProfilePicture
								height={height}
								picture={image}
								empty={canBeEmpty}
								dragging={dragging}
								fallbackType={fallbackType}
								shadowed={canBeEmpty}
								defaultUrl={!image ? defaultUrl : undefined}
							>
								{loading ? <ClipLoader /> : image ? getDeleteIcon() : canBeEmpty ? getAddIcon() : null}
							</StyledProfilePicture>
						) : (
							<StyledProfileBanner
								containerStyle={containerStyle}
								height={height}
								picture={image}
								defaultUrl={!image ? defaultUrl : undefined}
								dragging={dragging}
								shadowed={canBeEmpty}
							>
								{loading ? <ClipLoader /> : image?.url ? getDeleteIcon() : defaultUrl ? null : getAddIcon()}
							</StyledProfileBanner>
						)}
					</Row>
				) : (
					<>
						<Row style={{ marginBottom: 20 }}>
							{label && <PrimaryBold style={fontStyle ? fontStyle : undefined}>{label}</PrimaryBold>}
						</Row>
						<Row>
							{type === "avatar" ? (
								<StyledProfilePicture
									height={height}
									picture={image}
									empty={canBeEmpty}
									dragging={dragging}
									fallbackType={fallbackType}
									shadowed={canBeEmpty}
									defaultUrl={!image ? defaultUrl : undefined}
								>
									{loading ? <ClipLoader /> : image ? getDeleteIcon() : canBeEmpty ? getAddIcon() : null}
								</StyledProfilePicture>
							) : (
								<StyledProfileBanner
									containerStyle={containerStyle}
									height={height}
									picture={image}
									defaultUrl={!image ? defaultUrl : undefined}
									dragging={dragging}
									shadowed={canBeEmpty}
								>
									{loading ? <ClipLoader /> : image?.url ? getDeleteIcon() : defaultUrl ? null : getAddIcon()}
								</StyledProfileBanner>
							)}
						</Row>
					</>
				)
			}
		</UploadContainer>
	);
};

const UploadContainer = styled(FileUpload)`
	:active {
		opacity: 0.6;
	}
`;

const Row = styled.div`
	display: flex;
	align-items: center;
	${gap(26, "row")};

	> :first-child {
		min-width: 114px;
		max-width: 114px;

		@media ${mobile} {
			min-width: 177px;
			max-width: 177px;
		}
	}
`;

const StyledProfileBanner = styled(ProfileBanner).withConfig({
	shouldForwardProp: (prop) => !["dragging", "height"].includes(prop),
})<{ dragging: boolean; height?: number }>`
	position: relative;
	width: 100%;
	height: ${({ height }) => (height ? `${height}px` : "110px")};
	transition: transform 0.2s ease-in-out;
	transform: ${({ dragging }) => (dragging ? "scale(0.8)" : "initial")};
`;

const StyledProfilePicture = styled(ProfilePicture)<{ dragging: boolean; height?: number }>`
	position: relative;
	width: 110px;
	height: ${({ height }) => (height ? `${height}px` : "110px")};
	border-radius: 55px;
	transition: transform 0.2s ease-in-out;
	transform: ${({ dragging }) => (dragging ? "scale(0.6)" : "initial")};

	> svg {
		width: 30px;
		height: 30px;
	}
`;

const DeleteImageIcon = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "margin",
})<{ margin: number }>`
	cursor: pointer;
	position: absolute;
	right: ${({ margin }) => margin}px;
	top: ${({ margin }) => margin}px;
	z-index: ${Z.above(Z.modal)};

	@media (pointer: fine) {
		display: none;
	}

	animation: ${fadeIn} 0.2s linear;
	transition: transform 0.2s ease-in-out;
	&:hover {
		transform: rotate(90deg);
	}

	${UploadContainer}:hover & {
		display: block;
	}
`;

const AddIcon = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "iconOpacity",
})<{ iconOpacity?: number }>`
	display: flex;
	justify-content: center;
	align-items: center;
	height: 100%;
	opacity: ${({ iconOpacity }) => (iconOpacity ? `${iconOpacity}` : undefined)};
`;

const StyledPrimaryMedium = styled(PrimaryMedium)`
	font-size: 0.8rem;
`;
